import React from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Stethoscope, User, ArrowRight, ShieldCheck } from "lucide-react";

/**
 * PatientRoleBanner — показва се на профила на пациент за да го уведоми, че:
 *   1. Той е регистриран като пациент → НЯМА достъп до клинични инструменти
 *      (KEGG pathways, пълни SNP детайли, дозировки) — заради EU AI Act / FDA / CE
 *   2. Ако е здравен специалист → може да кандидатства за безплатен 30-day Clinician trial
 *
 * Скрива се за approved clinicians, admins, и users в активен Clinician trial.
 */
export default function PatientRoleBanner({ user }) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  // Hide for: admin, approved clinician, pending review, currently in clinician trial
  if (!user) return null;
  if (user.role === "admin") return null;
  if (user.role === "clinician" && user.clinician_status === "approved") return null;
  const sub = user.subscription || {};
  if (sub.plan_id === "clinic" && sub.status === "active") return null;
  if (user.trial_tier === "clinician") return null;

  const isPending = user.clinician_status === "pending_review";

  return (
    <div
      className={`rounded-xl p-4 border ${
        isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-100"
      }`}
      data-testid="patient-role-banner"
    >
      {/* Top: role chip */}
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          isDark ? "bg-violet-500/15" : "bg-violet-50"
        }`}>
          <User className="w-4 h-4 text-violet-500" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
              {bg ? "Регистриран като пациент" : "Registered as Patient"}
            </span>
            {isPending && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600">
                {bg ? "Заявка в преглед" : "Application pending"}
              </span>
            )}
          </div>
          <p className={`text-[11.5px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {bg ? "Образователен достъп · Patient view" : "Educational access · Patient view"}
          </p>
        </div>
      </div>

      {/* What patient view CANNOT access (compliance message) */}
      <div className={`rounded-lg p-3 mb-3 border text-[11.5px] flex items-start gap-2 ${
        isDark ? "bg-slate-500/10 border-slate-500/15" : "bg-slate-50 border-slate-100"
      }`}>
        <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-emerald-500" />
        <span className={isDark ? "text-slate-300" : "text-slate-600"}>
          {bg
            ? "Поради EU AI Act / FDA / CE регулациите, клиничните инструменти (пълни SNP, KEGG pathways, конкретни дозировки на добавки, Phenotype Synergy Detector) са достъпни само за здравни специалисти."
            : "Per EU AI Act / FDA / CE regulations, clinical tools (full SNPs, KEGG pathways, specific supplement dosages, Phenotype Synergy Detector) are restricted to healthcare professionals."}
        </span>
      </div>

      {/* CTA: apply for clinician trial */}
      {!isPending && (
        <button
          type="button"
          onClick={() => navigate("/clinician-trial-apply")}
          className={`w-full rounded-lg p-3 border-2 text-left transition-all hover:shadow-md ${
            isDark
              ? "bg-gradient-to-br from-rose-500/10 to-orange-500/10 border-rose-500/30 hover:border-rose-500/50"
              : "bg-gradient-to-br from-rose-50 to-orange-50 border-rose-200 hover:border-rose-300"
          }`}
          data-testid="apply-clinician-trial-btn"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center flex-shrink-0">
              <Stethoscope className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-[12px] font-bold mb-0.5 ${isDark ? "text-rose-200" : "text-rose-700"}`}>
                {bg ? "Лекар сте? Кандидатствайте за безплатен Clinician trial" : "Are you a clinician? Apply for free Clinician trial"}
              </p>
              <p className={`text-[11px] ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                {bg
                  ? "30 дни пълен достъп до клиничните инструменти · Без кредитна карта · Изисква проверка на медицински документ"
                  : "30 days full clinical access · No credit card · Medical document verification required"}
              </p>
            </div>
            <ArrowRight className={`w-4 h-4 flex-shrink-0 mt-1 ${isDark ? "text-rose-300" : "text-rose-500"}`} />
          </div>
        </button>
      )}

      {isPending && (
        <p className={`text-[11.5px] text-center ${isDark ? "text-amber-300" : "text-amber-700"}`}>
          {bg
            ? "Вашата заявка за Clinician trial се преглежда от admin team. Решението ще получите на email до 24 часа."
            : "Your Clinician trial application is being reviewed by admin team. You'll receive a decision by email within 24 hours."}
        </p>
      )}
    </div>
  );
}
